import { ApolloError } from "@apollo/client";
import Sentry from "@snapchat/sentry-browser";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { type BlizzardEventData } from "../../analytics/models";
import { WebLensUploadError } from "../api/putToSignedUrl";

/** Values of the `web_lens_stage` tag; one per place a flow can fail. */
export type WebLensStage = "upload" | "submit" | "update" | "rename" | "push";

export type WebLensFlow = "submit" | "update" | "rename" | "push";

/**
 * captureException with the web-lens tags (`feature:web_lens`, `web_lens_stage:<stage>`), so
 * Issues/Discover/alerts can slice by feature and stage. withScope keeps the tags per-event —
 * nothing leaks onto unrelated events captured later.
 */
export const captureWebLensException = (error: unknown, stage: WebLensStage): void => {
    Sentry.withScope((scope) => {
        scope.setTag("feature", "web_lens");
        scope.setTag("web_lens_stage", stage);
        Sentry.captureException(error);
    });
};

/** Low-cardinality values only — tags are indexed. Undefined entries are skipped. */
export type WebLensTagValues = Record<string, string | boolean | undefined>;

export interface WebLensFlowTrace {
    /** Opens a stage child span; returns its idempotent finisher. */
    startStage: (stage: "upload" | "mutation") => () => void;
    /** Adds indexed tags to the attempt's transaction (e.g. program flags, failure reason). */
    setTags: (tags: WebLensTagValues) => void;
    /** Tags the outcome (the Blizzard label verbatim), closes open stages, ends the transaction. */
    finish: (outcomeLabel: string) => void;
}

/**
 * One Sentry transaction per flow attempt (`web_lens.submit|update|push`) — the route-level
 * pageload/navigation transaction has idled out long before a creator picks a file and clicks
 * Submit, so without one the attempt has no transaction at all. Scope-binding it makes
 * BrowserTracing attach the GCS PUTs and the GraphQL POST as child spans and carries the trace
 * id into the BFF.
 */
export const startWebLensFlowTrace = (flow: WebLensFlow): WebLensFlowTrace => {
    const transaction = Sentry.startTransaction({
        name: `web_lens.${flow}`,
        op: "web_lens.flow",
        tags: { feature: "web_lens", web_lens_flow: flow },
    });
    const scope = Sentry.getCurrentHub().getScope();
    const previousSpan = scope.getSpan();
    scope.setSpan(transaction);

    const openStages: (() => void)[] = [];
    return {
        setTags: (tags) => {
            for (const [key, value] of Object.entries(tags)) {
                if (value !== undefined) {
                    transaction.setTag(key, String(value));
                }
            }
        },
        startStage: (stage) => {
            const span = transaction.startChild({ op: `web_lens.${stage}`, description: `${flow}: ${stage}` });
            let ended = false;
            const end = (): void => {
                if (!ended) {
                    ended = true;
                    span.finish();
                }
            };
            openStages.push(end);
            return end;
        },
        finish: (outcomeLabel) => {
            // A stage left open (mutation, or upload on its failure path) ends with the attempt.
            for (const endStage of openStages) {
                endStage();
            }
            transaction.setTag("web_lens_outcome", outcomeLabel);
            transaction.setStatus(outcomeLabel.endsWith("_success") ? "ok" : "unknown_error");
            transaction.finish();
            // Restore only if nothing newer (a navigation transaction) already took the scope over.
            if (scope.getSpan() === transaction) {
                scope.setSpan(previousSpan);
            }
        },
    };
};

export interface WebLensAttempt {
    trace: WebLensFlowTrace;
    /**
     * Fires the attempt's single outcome: finishes the trace and emits the Blizzard label. A
     * failure reason (see uploadFailureReason / failedCheckReason) lands on the transaction as
     * `web_lens_failure_reason` — the only place a failed attempt's "why" is countable, since
     * creator-fault failures deliberately produce no Sentry error event.
     */
    sendOutcome: (label: string, failureReason?: string) => void;
}

/**
 * Attempt-owned instrumentation for one submit/update/push invocation. Exactly one outcome label
 * fires per attempt, sharing the attempt label's prefix so the funnel groups by substring; the
 * upload/server split exists because the browser→GCS leg touches no backend — these events are
 * the only place upload failures are countable (Sentry deliberately ignores creator-environment
 * upload faults). sendOutcome finishes the trace with the same label the Blizzard funnel gets,
 * so the two views cannot disagree. Locals of the invocation, never hook state: a re-entrant
 * call must not be able to finish another attempt's trace.
 */
export const startWebLensAttempt = (
    flow: WebLensFlow,
    sendBlizzardEvent: (event: BlizzardEventData) => void,
    /** Segmentation tags known at attempt start, e.g. program enrollment flags. */
    tags: WebLensTagValues = {},
): WebLensAttempt => {
    const trace = startWebLensFlowTrace(flow);
    trace.setTags(tags);
    return {
        trace,
        sendOutcome: (label, failureReason) => {
            if (failureReason) {
                trace.setTags({ web_lens_failure_reason: failureReason });
            }
            trace.finish(label);
            sendBlizzardEvent({
                event: "MYLENSES_ACTION",
                actionType: myLensesActionType.submit,
                label,
                namespace: "lenses",
            });
        },
    };
};

/**
 * Upload-stage reason. PUT failures carry their transport kind (`network`, `stalled`,
 * `file_changed`) or `http_<status>`. The ticket
 * mutation rejects on GraphQL/network errors (the app's default errorPolicy), so a BFF refusal
 * surfaces as `ticket_<code>` — `ticket_forbidden` is the COF gate, `ticket_rate_limited` the
 * throttle — rather than being lumped into `unexpected`.
 */
export const uploadFailureReason = (error: unknown): string => {
    if (error instanceof WebLensUploadError) {
        return error.kind === "http" ? `http_${error.status ?? "unknown"}` : error.kind;
    }
    if (error instanceof ApolloError) {
        const code: unknown = error.graphQLErrors[0]?.extensions["code"];
        if (typeof code === "string") {
            return `ticket_${code.toLowerCase()}`;
        }
        return error.networkError ? "ticket_network" : "ticket_error";
    }
    return "unexpected";
};

/** First failed row's id, normalized (`package-type` → `package_type`, `CREATE_LENS` → `create_lens`). */
export const failedCheckReason = (checks: readonly { id: string; state: string }[]): string =>
    (checks.find((check) => check.state === "fail")?.id ?? "unknown").toLowerCase().replaceAll("-", "_");
